import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { dataService } from '../../services/dataService';
import { HistoryRecord, AgeGroup } from '../../types/domain';
import {
  BodySide,
  GlucoseZone,
  InjectionSite,
  getSitesForAge,
  getSuggestedSiteCount,
  getGlucoseTypeOptions,
  getInsulinTypeOptions,
  getSiteClass,
  getGlucoseStatusText,
  getSiteLabelForHistory,
  formatDateTime,
  getAgeGroupLabel
} from '../../lib/domain-data';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { cn } from '../../lib/utils';

type Scenario = 'check-glucose' | 'inject-insulin';

export default function Home() {
  const { user, profile } = useAuth();
  const [ageGroup, setAgeGroup] = useState<AgeGroup | null>(null);
  const [scenario, setScenario] = useState<Scenario | null>(null);
  const [bodySide, setBodySide] = useState<BodySide>('front');
  const [suggestedSiteId, setSuggestedSiteId] = useState<number | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [glucoseValue, setGlucoseValue] = useState<number | null>(100);
  const [glucoseZone, setGlucoseZone] = useState<GlucoseZone | null>(null);
  const [glucoseTypeId, setGlucoseTypeId] = useState<string | null>(null);
  const [insulinTypeId, setInsulinTypeId] = useState<string | null>(null);
  const [completionMessage, setCompletionMessage] = useState<string | null>(null);
  const [injectionDate, setInjectionDate] = useState<string>('');
  const [injectionTime, setInjectionTime] = useState<string>('');
  const [insulinName, setInsulinName] = useState<string>('');
  const [insulinDose, setInsulinDose] = useState<number | ''>(1);

  const [isSavingInjection, setIsSavingInjection] = useState(false);
  const [saveInjectionError, setSaveInjectionError] = useState<string | null>(null);
  const [showLegend, setShowLegend] = useState(false);
  const [lastActivity, setLastActivity] = useState<HistoryRecord | null>(null);

  useEffect(() => {
    if (user) {
      dataService.getHistory(user.id).then(history => {
        if (history.length > 0) {
          setLastActivity(history[0]);
        }
      });
    }
  }, [user, scenario]);

  useEffect(() => {
    if (user) {
      dataService.migrateLocalData(user.id);
      dataService.syncOfflineData(user.id);

      const handleOnline = () => {
        dataService.syncOfflineData(user.id);
      };

      window.addEventListener('online', handleOnline);
      return () => {
        window.removeEventListener('online', handleOnline);
      };
    }
    return undefined;
  }, [user]);

  useEffect(() => {
    const now = new Date();
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
    setInjectionDate(local.toISOString().slice(0, 10));
    setInjectionTime(local.toISOString().slice(11, 16));
  }, []);

  // Automatically calculate age group from profile
  useEffect(() => {
    if (profile?.birth_date) {
      const birthDate = new Date(profile.birth_date);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      const calculatedGroup: AgeGroup = age < 11 ? '2-10' : '11-plus';

      // Only set if different to avoid infinite loops and unnecessary re-renders
      if (ageGroup !== calculatedGroup) {
        setAgeGroup(calculatedGroup);
        const sites = getSitesForAge(calculatedGroup);
        // Only load initial suggestion if we just determined the age group
        // We don't want to reset it if it was already set
        if (!suggestedSiteId) {
          loadInitialSuggestion(calculatedGroup, sites);
        }
      }
    }
  }, [profile, ageGroup, suggestedSiteId]);

  const currentSites = useMemo(() => getSitesForAge(ageGroup), [ageGroup]);

  const resetState = () => {
    setScenario(null);
    setBodySide('front');
    setSelectedSiteId(null);
    setGlucoseValue(100);
    setGlucoseZone(null);
    setGlucoseTypeId(null);
    setInsulinTypeId(null);
    setCompletionMessage(null);
  };

  const loadInitialSuggestion = async (nextAgeGroup: AgeGroup | null, sites: InjectionSite[]) => {
    if (!nextAgeGroup || !user) {
      setSuggestedSiteId(null);
      return;
    }
    const nextId = await dataService.getLastSite(user.id, nextAgeGroup);

    let suggestion = 1;
    const count = sites.length;

    if (nextId && nextId >= 1 && nextId <= count) {
      suggestion = (nextId % count) + 1;
    }

    setSuggestedSiteId(suggestion);
    const site = sites.find(s => s.id === suggestion);
    if (site) {
      setBodySide(site.side);
    }
  };

  const handleSelectScenario = (nextScenario: Scenario) => {
    setScenario(nextScenario);
    setSelectedSiteId(null);
    setCompletionMessage(null);
  };

  const handleSelectGlucoseType = (id: string) => {
    setGlucoseTypeId(id);
  };

  const handleGlucoseChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    if (!value || value <= 0) {
      setGlucoseValue(null);
      setGlucoseZone(null);
      return;
    }
    setGlucoseValue(value);
    if (value < 70) {
      setGlucoseZone('low');
    } else if (value <= 150) {
      setGlucoseZone('normal');
    } else if (value <= 250) {
      setGlucoseZone('warning');
    } else {
      setGlucoseZone('high');
    }
  };

  const handleGlucoseNext = (needsInjection: boolean) => {
    if (needsInjection) {
      handleSelectScenario('inject-insulin');
    } else {
      // reset step
      resetState();
    }
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInjectionDate(event.target.value);
  };

  const handleTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInjectionTime(event.target.value);
  };

  const handleSelectInsulinType = (id: string) => {
    setInsulinTypeId(id);
  };

  const handleToggleSide = (side: BodySide) => {
    setBodySide(side);
  };

  const handleSelectSite = (id: number) => {
    setSelectedSiteId(id);
    setCompletionMessage(null);
  };

  const handleSaveInjection = async () => {
    if (!ageGroup || !selectedSiteId || isSavingInjection || !user) {
      return;
    }
    if (!injectionDate || !injectionTime || !insulinName || insulinDose === '' || !insulinTypeId) {
      setSaveInjectionError('Mohon lengkapi semua data suntikan.');
      return;
    }

    setIsSavingInjection(true);
    setSaveInjectionError(null);
    try {
      await dataService.addInjection(user.id, {
        date: injectionDate,
        time: injectionTime,
        siteId: selectedSiteId,
        insulinTypeId,
        ageGroup,
        insulinBrand: insulinName,
        insulinDose: Number(insulinDose),
        glucoseValue: glucoseValue ?? undefined
      });

      await dataService.updateLastSite(user.id, ageGroup, selectedSiteId);

      const count = getSuggestedSiteCount(ageGroup);
      if (!count) {
        return;
      }
      const currentSelectedId = selectedSiteId;
      const nextId = (currentSelectedId % count) + 1;
      const nextSite = currentSites.find(site => site.id === nextId);
      if (nextSite) {
        setCompletionMessage(
          `Suntikan berikutnya disarankan di lokasi ${nextSite.id}: ${nextSite.label}.`
        );
        setSuggestedSiteId(nextSite.id);
        setBodySide(nextSite.side);
      }
      setSelectedSiteId(null);
      setAgeGroup(null);
      resetState();
    } catch {
      setSaveInjectionError('Terjadi kesalahan saat menyimpan riwayat suntikan.');
    } finally {
      setIsSavingInjection(false);
    }
  };



  const glucoseStatusText = getGlucoseStatusText(glucoseZone);

  const renderGlucoseStatus = () => {
    if (!glucoseZone || glucoseValue === null || !glucoseStatusText) {
      return null;
    }
    return (
      <div className={`glucose-status ${glucoseZone}`}>
        <div>{glucoseStatusText}</div>
        <div>{glucoseValue} mg/dL</div>
      </div>
    );
  };



  const renderModeStep = () => {
    return (
      <div
        className="screen-card"
        role="group"
        aria-labelledby="mode-step-title"
      >
        <div className="mb-6 pb-4 border-b border-slate-100">
          <div className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
            Halo, {profile?.full_name || 'Adik'}! <span className="animate-wave">👋</span>
          </div>

          {lastActivity ? (
            <Card className="mb-6 bg-white/60 backdrop-blur-sm border-indigo-50 shadow-sm">
              <CardHeader className="pb-2 pt-4 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-bold text-indigo-500 uppercase tracking-wider flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></span>
                    Aktivitas Terakhir
                  </CardTitle>
                  <Badge variant="outline" className="font-normal text-xs text-slate-500 border-indigo-100 bg-white/50">
                    {formatDateTime(lastActivity.date, lastActivity.time)}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0 space-y-3">
                <div className="flex justify-between items-start border-b border-indigo-50/50 pb-2">
                  <span className="text-xs text-slate-500 font-medium shrink-0 mt-0.5">Lokasi</span>
                  <span className="text-sm font-semibold text-slate-700 text-right leading-tight max-w-[70%]">
                    {getSiteLabelForHistory(lastActivity.ageGroup, lastActivity.siteId)}
                  </span>
                </div>

                {lastActivity.insulinBrand && (
                  <div className="flex justify-between items-center border-b border-indigo-50/50 pb-2">
                    <span className="text-xs text-slate-500 font-medium">Merk</span>
                    <span className="text-sm font-semibold text-slate-700">
                      {lastActivity.insulinBrand}
                    </span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 pt-1">
                  {lastActivity.insulinDose !== undefined && lastActivity.insulinDose !== null && (
                    <div className="bg-blue-50/50 rounded-xl p-3 text-center border border-blue-100/50">
                      <div className="text-[10px] text-blue-400 font-bold uppercase mb-0.5">Dosis</div>
                      <div className="text-lg font-bold text-blue-700">
                        {lastActivity.insulinDose} <span className="text-xs font-medium opacity-70">unit</span>
                      </div>
                    </div>
                  )}

                  {lastActivity.glucoseValue !== undefined && lastActivity.glucoseValue !== null && (
                    <div className={cn(
                      "rounded-xl p-3 text-center border",
                      lastActivity.glucoseValue < 70 ? 'bg-red-50/50 border-red-100/50 text-red-700' :
                        lastActivity.glucoseValue > 250 ? 'bg-orange-50/50 border-orange-100/50 text-orange-700' :
                          'bg-emerald-50/50 border-emerald-100/50 text-emerald-700'
                    )}>
                      <div className="text-[10px] font-bold uppercase mb-0.5 opacity-70">Gula Darah</div>
                      <div className="text-lg font-bold">
                        {lastActivity.glucoseValue} <span className="text-xs font-medium opacity-70">mg/dL</span>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="mb-6 bg-blue-50 border-blue-100">
              <CardContent className="p-4 flex items-center gap-3">
                <span className="text-xl">✨</span>
                <span className="font-medium text-blue-800 text-sm">Belum ada riwayat suntikan. Yuk mulai!</span>
              </CardContent>
            </Card>
          )}
        </div>

        <div id="mode-step-title" className="section-title">
          Mau apa sekarang?
        </div>
        <div className="subtitle">Pilih permainan yang sesuai kegiatan Adik.</div>
        <div className="mode-grid">
          <button
            type="button"
            className={`mode-card ${scenario === 'check-glucose' ? 'selected' : ''}`}
            onClick={() => handleSelectScenario('check-glucose')}
          >
            Cek gula darah
          </button>
          <button
            type="button"
            className={`mode-card ${scenario === 'inject-insulin' ? 'selected' : ''}`}
            onClick={() => handleSelectScenario('inject-insulin')}
          >
            Suntik insulin
          </button>
        </div>
      </div>
    );
  };

  const renderGlucoseScenario = () => {
    const options = getGlucoseTypeOptions(ageGroup);

    return (
      <div
        className="screen-card"
        role="group"
        aria-labelledby="glucose-step-title"
      >
        <div id="glucose-step-title" className="section-title">
          Cek gula darah
        </div>
        <div className="steps">
          <div className="step-pill active">1. Waktu cek</div>
          <div className="step-pill">2. Angka gula</div>
          <div className="step-pill">3. Lanjut?</div>
        </div>

        <div className="subtitle">1. Pilih waktu pemeriksaan</div>
        <div className="option-grid mb-3">
          {options.map(option => (
            <button
              key={option.id}
              type="button"
              className={`chip ${glucoseTypeId === option.id ? 'selected' : ''}`}
              onClick={() => handleSelectGlucoseType(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="subtitle">2. Masukkan hasil gula darah</div>
        <div className="flex flex-col items-center">
          <input
            type="number"
            inputMode="numeric"
            className="value-display w-40 bg-transparent border-b-2 border-slate-200 focus:border-indigo-500 outline-none transition-colors p-0 appearance-none m-0"
            value={glucoseValue ?? ''}
            placeholder="---"
            onChange={handleGlucoseChange}
            aria-label="Masukkan nilai gula darah"
          />
        </div>
        <div className="value-label">mg/dL</div>
        <input
          type="range"
          min={40}
          max={400}
          step={1}
          onChange={handleGlucoseChange}
        />

        {renderGlucoseStatus()}

        <div className="subtitle mt-2">
          3. Perlu lanjut ke suntik insulin?
        </div>
        <div className="next-actions">
          <button
            type="button"
            className="btn-secondary w-full md:w-auto"
            onClick={() => handleGlucoseNext(false)}
            aria-label="Tidak lanjut suntik insulin sekarang"
          >
            Tidak sekarang
          </button>
          <button
            type="button"
            className="btn-primary w-full md:w-auto"
            onClick={() => handleGlucoseNext(true)}
            aria-label="Lanjut ke langkah suntik insulin"
          >
            Lanjut suntik
          </button>
        </div>
      </div>
    );
  };

  const renderLegendModal = () => {
    if (!showLegend || !ageGroup) return null;

    const sortedSites = [...currentSites].sort((a, b) => a.id - b.id);

    return (
      <div className="modal-overlay" onClick={() => setShowLegend(false)}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="m-0 text-[18px]">Keterangan ({getAgeGroupLabel(ageGroup)})</h3>
            <button
              onClick={() => setShowLegend(false)}
              className="bg-transparent border-none text-[24px] cursor-pointer px-2"
              aria-label="Tutup keterangan"
            >
              &times;
            </button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            <ol className="pl-5 m-0">
              {sortedSites.map((site, index) => (
                <li key={site.id} className="mb-2 text-[14px]">
                  {index + 1}. {site.label}
                </li>
              ))}
            </ol>
            {ageGroup === '2-10' && (
              <p className="mt-4 text-[13px] text-[#666] italic border-t border-[#eee] pt-3">
                Hindari menyuntikkan insulin di bagian dalam paha, bagian dalam lengan, area yang terlalu dekat dengan pusar, dan pundak
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderBodyMap = () => {
    const sites = currentSites
      .filter(site => site.side === bodySide)
      .map(site => ({
        ...site,
        x: bodySide === 'back' ? 100 - site.x : site.x
      }));
    const imageHref =
      bodySide === 'front'
        ? '/assets/images/body-front.png'
        : '/assets/images/body-rear.png';

    const suggestedSite =
      suggestedSiteId !== null
        ? currentSites.find(site => site.id === suggestedSiteId)
        : undefined;

    const selectedSite =
      selectedSiteId !== null
        ? sites.find(site => site.id === selectedSiteId)
        : undefined;

    return (
      <div className="body-card">
        <div className="side-toggle">
          <button
            type="button"
            className={`side-button ${bodySide === 'front' ? 'active' : ''}`}
            onClick={() => handleToggleSide('front')}
          >
            Depan
          </button>
          <button
            type="button"
            className={`side-button ${bodySide === 'back' ? 'active' : ''}`}
            onClick={() => handleToggleSide('back')}
          >
            Belakang
          </button>
        </div>

        <div className="relative w-full max-w-[400px]">
          <svg
            className="body-svg block w-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="xMidYMax meet"
          >
            <image href={imageHref} x={26} y={0} height={110} />
            {sites.map(site => (
              <g
                key={site.id}
                className={getSiteClass(site, suggestedSiteId, selectedSiteId)}
                onClick={() => handleSelectSite(site.id)}
              >
                <circle
                  cx={site.x}
                  cy={site.y}
                  r={site.id === suggestedSiteId ? 5 : 4}
                />
                <text
                  x={site.x}
                  y={site.y + 1.2}
                  textAnchor="middle"
                >
                  {site.id}
                </text>
              </g>
            ))}
          </svg>
          {selectedSite ? (
            <div
              className="flying-text -translate-x-1/2 -translate-y-full -mt-[10px]"
              style={{
                left: `${selectedSite.x}%`,
                top: `${selectedSite.y}%`,
                marginLeft: selectedSite.x > 50 ? '-10px' : '10px'
              }}
            >
              {selectedSite.label}
            </div>
          ) : null}
        </div>

        <div className="w-full flex justify-center">
          <button
            type="button"
            className="btn-secondary px-4 py-1.5 text-[13px] rounded-[20px]"
            onClick={() => setShowLegend(true)}
          >
            Keterangan Gambar
          </button>
        </div>

        {suggestedSite ? (
          <div className="suggestion-text">
            <strong>Saran lokasi sekarang: {suggestedSite.id}</strong>
            <span>{suggestedSite.label}</span>
            <div className="text-[13px] italic mt-1 text-[#666]">
              (apabila lokasi suntik sebelumnya sama, maka dapat dipindah)
            </div>
          </div>
        ) : null}

        {renderLegendModal()}
      </div>
    );
  };

  const renderInjectionScenario = () => {
    const insulinOptions = getInsulinTypeOptions();
    const canSave = selectedSiteId !== null;

    return (
      <>
        <div
          className="screen-card"
          role="group"
          aria-labelledby="inject-step-title"
        >
          <div id="inject-step-title" className="section-title">
            Suntik insulin
          </div>
          <div className="steps">
            <div className="step-pill active">1. Jenis insulin</div>
            <div className="step-pill">2. Lokasi suntik</div>
            <div className="step-pill">3. Simpan</div>
          </div>

          <div className="field-group">
            <div className="field">
              <label htmlFor="injection-date">Tanggal <span className="text-red-500">*</span></label>
              <input
                id="injection-date"
                type="date"
                value={injectionDate}
                onChange={handleDateChange}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="injection-time">Jam <span className="text-red-500">*</span></label>
              <input
                id="injection-time"
                type="time"
                value={injectionTime}
                onChange={handleTimeChange}
                required
              />
            </div>
          </div>

          <div className="field-group">
            <div className="field">
              <label htmlFor="insulin-name">Nama insulin <span className="text-red-500">*</span></label>
              <input
                id="insulin-name"
                type="text"
                placeholder="Contoh: Levemir"
                value={insulinName}
                onChange={(e) => setInsulinName(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="insulin-dose">Dosis (unit) <span className="text-red-500">*</span></label>
              <input
                id="insulin-dose"
                type="number"
                min={0}
                inputMode="decimal"
                value={insulinDose}
                onChange={(e) => setInsulinDose(e.target.value === '' ? '' : Number(e.target.value))}
                required
              />
            </div>
          </div>

          <div className="subtitle">1. Pilih jenis suntikan <span className="text-red-500">*</span></div>
          <div className="option-grid mb-3">
            {insulinOptions.map(option => (
              <button
                key={option.id}
                type="button"
                className={`chip ${insulinTypeId === option.id ? 'selected' : ''}`}
                onClick={() => handleSelectInsulinType(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div
          className="screen-card flex-1 flex flex-col min-h-fit"
          role="group"
          aria-labelledby="inject-location-title"
        >
          <div id="inject-location-title" className="section-title text-[28px] mb-2 font-extrabold pb-1">
            Pilih lokasi suntikan
          </div>
          <div className="subtitle text-[16px]">
            2. Pilih titik di tubuh Adik kemudian simpan.
          </div>

          {renderBodyMap()}

          <div className="next-actions mt-4">
            <button
              type="button"
              className="btn-primary w-full md:w-auto"
              disabled={!canSave || isSavingInjection}
              aria-disabled={!canSave || isSavingInjection}
              aria-label="Simpan lokasi suntikan"
              onClick={handleSaveInjection}
            >
              {isSavingInjection ? 'Menyimpan...' : 'Simpan lokasi suntikan'}
            </button>
          </div>

          {completionMessage ? (
            <div className="completion-message">{completionMessage}</div>
          ) : null}
          {saveInjectionError ? (
            <div className="subtitle mt-2 text-red-700">
              {saveInjectionError}
            </div>
          ) : null}
        </div>
      </>
    );
  };

  const renderContent = () => {
    if (ageGroup === null) {
      return (
        <div className="flex justify-center items-center min-h-[50vh]">
          <div className="text-xl text-gray-600">Menyiapkan data adik...</div>
        </div>
      );
    }
    if (scenario === null) {
      return renderModeStep();
    }
    if (scenario === 'check-glucose') {
      return renderGlucoseScenario();
    }
    return renderInjectionScenario();
  };

  return (
    <div className="screen min-h-[100dvh] h-full">
      {renderContent()}
    </div>
  );
}
